/**
 * The session: who is signed in, what they may do, and how the app looks.
 *
 * One provider rather than three, because these facts are genuinely coupled —
 * appearance depends on the profile, the profile depends on the auth user, and
 * "Test as Student" changes the answer to every permission question. Splitting
 * them would mean three loading states that must not disagree.
 *
 * The important idea here is `viewAsStudent`. It is a *view* flag, stored in
 * sessionStorage and never written to the account. Admin's real role, real
 * enrollment records and real permissions are untouched; the flag only makes
 * every `can()` and every content query answer as a student would. That is why
 * the preview is trustworthy: nothing is simulated, the same predicates simply
 * receive a different principal.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';

import {
  auth,
  db,
  emailIsOnDomain,
  onAuthStateChanged,
  profilePhotoUrl,
  type User,
} from '../lib/firebase';
import {
  applyAppearance,
  coercePrefs,
  DEFAULT_PREFS,
  readCachedPrefs,
  watchSystemTheme,
  writeCachedPrefs,
} from '../design/theme';
import type { UserPrefs } from '../domain/types';
import { recordLogin } from '../data/callables';
import { refreshPushRegistration } from '../lib/push';
import {
  can as canDo,
  isAdmin as principalIsAdmin,
  isFamilyAccount,
  type Capability,
  type Principal,
  type Role,
} from '../../shared/authz';

const VIEW_AS_KEY = 'ss.viewAsStudent';

export type SessionStatus =
  /** Firebase has not yet said whether anybody is signed in. */
  | 'initialising'
  /** Nobody is signed in. */
  | 'signed-out'
  /** Signed in, but not with an @slr.family account. */
  | 'wrong-domain'
  | 'ready';

interface SessionValue {
  status: SessionStatus;
  user: User | null;
  /** The authorization subject. Null until a family account is signed in. */
  principal: Principal | null;
  /** Admin's true role, ignoring preview mode. */
  actualRole: Role | null;
  /** The role the interface should behave as. Differs only while previewing. */
  effectiveRole: Role | null;
  displayName: string;
  firstName: string;
  photoURL: string | null;

  prefs: UserPrefs;
  savePrefs: (patch: Partial<UserPrefs>) => Promise<void>;
  /** Paints the interior of a semester in its own color. Null resets. */
  setAccentOverride: (accent: string | null) => void;

  viewAsStudent: boolean;
  setViewAsStudent: (on: boolean) => void;
  /** Capability check. Answers as a student while previewing. */
  can: (capability: Capability) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

function readViewAs(): boolean {
  try {
    return window.sessionStorage.getItem(VIEW_AS_KEY) === '1';
  } catch {
    return false;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('initialising');
  const [user, setUser] = useState<User | null>(null);
  const [prefs, setPrefs] = useState<UserPrefs>(readCachedPrefs);
  const [viewAsStudent, setViewAsStudentState] = useState<boolean>(readViewAs);
  const [accentOverride, setAccentOverride] = useState<string | null>(null);

  // Guards a redundant profile write on every auth state replay.
  const profileWrittenFor = useRef<string | null>(null);

  // ── Auth ──────────────────────────────────────────────────────────────────

  useEffect(
    () =>
      onAuthStateChanged(auth, (next) => {
        if (!next) {
          setUser(null);
          setStatus('signed-out');
          profileWrittenFor.current = null;
          return;
        }
        if (!emailIsOnDomain(next.email)) {
          // Kept signed in deliberately: the sign-in screen needs to be able to
          // say *why* it is refusing, which it cannot do after a sign-out.
          setUser(next);
          setStatus('wrong-domain');
          return;
        }
        setUser(next);
        setStatus('ready');
      }),
    []
  );

  /*
   * Keep the account's directory record current.
   *
   * This keeps a signed-in person's directory profile current. Enrollment can
   * also pre-provision the directory entry before a first visit, so a newly
   * invited family address is immediately available to notification groups.
   * Only fields the rules permit are written; role and activity belong to
   * Cloud Functions.
   */
  useEffect(() => {
    if (status !== 'ready' || !user) return;
    if (profileWrittenFor.current === user.uid) return;
    profileWrittenFor.current = user.uid;

    void setDoc(
      doc(db, 'users', user.uid),
      {
        displayName: user.displayName ?? '',
        photoURL: profilePhotoUrl(user) ?? '',
        seenAt: serverTimestamp(),
      },
      { merge: true }
    ).catch(() => {
      // Non-critical: a failed directory refresh must never block sign-in.
    });

    /*
     * Two server-side follow-ups, both fire-and-forget.
     *
     * `recordLogin` writes the activity trail entry — server-side, so it
     * cannot be forged or skipped by a client that would rather not be
     * logged. `refreshPushRegistration` re-asserts this device's push
     * subscription, because push services rotate endpoints and a stale one
     * silently stops delivering.
     */
    void recordLogin({}).catch(() => {});
    void refreshPushRegistration();
  }, [status, user]);

  // ── Preferences ───────────────────────────────────────────────────────────

  /*
   * Reconcile the cached preferences with the account's.
   *
   * The cache has already painted; this only corrects it, which is why there
   * is no loading state around appearance anywhere in the app.
   */
  useEffect(() => {
    if (status !== 'ready' || !user) return;
    return onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        const stored = snap.data()?.prefs;
        if (!stored) return;
        const merged = coercePrefs(stored);
        setPrefs(merged);
        writeCachedPrefs(merged);
      },
      () => {
        // Offline or refused: the cached preferences remain in force.
      }
    );
  }, [status, user]);

  useEffect(() => {
    applyAppearance(prefs, accentOverride);
  }, [prefs, accentOverride]);

  // Follow the OS only while the preference actually says "system".
  useEffect(() => {
    if (prefs.theme !== 'system') return;
    return watchSystemTheme(() => applyAppearance(prefs, accentOverride));
  }, [prefs, accentOverride]);

  const savePrefs = useCallback(
    async (patch: Partial<UserPrefs>) => {
      const next = coercePrefs({ ...prefs, ...patch });
      // Optimistic: appearance is a preference, not a transaction. If the write
      // fails the user still gets what they asked for on this device.
      setPrefs(next);
      writeCachedPrefs(next);
      if (!user) return;
      await setDoc(doc(db, 'users', user.uid), { prefs: next }, { merge: true }).catch(() => {});
    },
    [prefs, user]
  );

  // ── Preview mode ──────────────────────────────────────────────────────────

  const setViewAsStudent = useCallback((on: boolean) => {
    setViewAsStudentState(on);
    try {
      if (on) window.sessionStorage.setItem(VIEW_AS_KEY, '1');
      else window.sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      /* the flag still holds for this tab in memory */
    }
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────

  const value = useMemo<SessionValue>(() => {
    const principal: Principal | null =
      user && user.email
        ? { uid: user.uid, email: user.email, emailVerified: user.emailVerified }
        : null;

    const family = isFamilyAccount(principal);
    const actualRole: Role | null = family ? (principalIsAdmin(principal) ? 'admin' : 'student') : null;

    // Only Admin can be previewing; for everyone else the flag is meaningless.
    const previewing = actualRole === 'admin' && viewAsStudent;
    const effectiveRole: Role | null = previewing ? 'student' : actualRole;

    const displayName = user?.displayName?.trim() || user?.email?.split('@')[0] || 'there';

    return {
      status,
      user,
      principal,
      actualRole,
      effectiveRole,
      displayName,
      firstName: displayName.split(' ')[0] ?? displayName,
      photoURL: profilePhotoUrl(user),
      prefs,
      savePrefs,
      setAccentOverride,
      viewAsStudent: previewing,
      setViewAsStudent,
      can: (capability: Capability) => (previewing ? false : canDo(principal, capability)),
    };
  }, [status, user, prefs, savePrefs, viewAsStudent, setViewAsStudent]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** Convenience for the common case. Answers as a student while previewing. */
export function useIsAdmin(): boolean {
  return useSession().effectiveRole === 'admin';
}

export { DEFAULT_PREFS };
